import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
/**
 * KTK-317 — edit a channel's name, description, and subscribed agents in
 * one modal. Opened via the sidebar row's context menu (`right-click →
 * Edit channel`). `channel_id` is the row's primary key on the Hub side
 * so the rename is non-destructive — history / watermarks / pending
 * decisions survive.
 *
 * Mounts in three phases:
 *   1. fetch the workspace agent list (for the subscriber multi-select)
 *      and the channel's current subscribers
 *   2. user edits any subset of fields; "Save" enabled only when at
 *      least one field actually differs from the loaded baseline
 *   3. apply changes — name PATCH, description PATCH, subscribers PUT —
 *      each only when its field changed; on success fire `onSaved` so
 *      the parent can re-fetch the channel cache + close
 */
export function EditChannelDialog({ workspaceId, channel, onClose, onSaved }) {
    const ctx = useDataContext();
    // Inputs ----------------------------------------------------------------
    const [name, setName] = useState(channel.name);
    const [description, setDescription] = useState(channel.description ?? "");
    // Agents -- workspace roster + current subscribers ----------------------
    const [allAgents, setAllAgents] = useState(null);
    const [initialSubscriberIds, setInitialSubscriberIds] = useState(null);
    const [subscriberIds, setSubscriberIds] = useState(new Set());
    const [loadError, setLoadError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setLoadError(null);
        Promise.all([
            ctx.hub.listAgents(workspaceId),
            ctx.hub.getChannelSubscribers(workspaceId, channel.id),
        ])
            .then(([agents, subs]) => {
            if (cancelled)
                return;
            setAllAgents(agents);
            const subIds = new Set(subs.map((a) => a.id));
            setInitialSubscriberIds(subIds);
            setSubscriberIds(new Set(subIds));
        })
            .catch((e) => {
            if (!cancelled) {
                setLoadError(e instanceof Error ? e.message : String(e));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [ctx, workspaceId, channel.id]);
    const toggleSub = (agentId) => {
        setSubscriberIds((prev) => {
            const next = new Set(prev);
            if (next.has(agentId))
                next.delete(agentId);
            else
                next.add(agentId);
            return next;
        });
    };
    // Dirty checks ----------------------------------------------------------
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const nameDirty = trimmedName !== channel.name;
    const descDirty = trimmedDescription !== (channel.description ?? "").trim();
    const subsDirty = useMemo(() => {
        if (!initialSubscriberIds)
            return false;
        if (initialSubscriberIds.size !== subscriberIds.size)
            return true;
        for (const id of subscriberIds) {
            if (!initialSubscriberIds.has(id))
                return true;
        }
        return false;
    }, [initialSubscriberIds, subscriberIds]);
    const anyDirty = nameDirty || descDirty || subsDirty;
    // KTK-317 — v1 agents (Claude Code) bake KRITAKA_SUBSCRIPTIONS at spawn
    // time and need a restart to see a new subscription. Surface a hint
    // when any v1 agent was newly added so the operator knows to bounce
    // those sessions. (V2 agents pick up live.)
    const newlyAddedV1Names = useMemo(() => {
        if (!allAgents || !initialSubscriberIds)
            return [];
        const added = [];
        for (const id of subscriberIds) {
            if (initialSubscriberIds.has(id))
                continue;
            const a = allAgents.find((x) => x.id === id);
            if (a && (a.runtime_kind ?? "claude-code") === "claude-code") {
                added.push(a.name);
            }
        }
        return added;
    }, [allAgents, initialSubscriberIds, subscriberIds]);
    // Submit ----------------------------------------------------------------
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const onSubmit = async (e) => {
        e.preventDefault();
        if (!anyDirty || submitting)
            return;
        if (!trimmedName) {
            setError("Channel name is required.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            if (nameDirty) {
                await ctx.hub.updateChannelName(workspaceId, channel.id, trimmedName);
            }
            if (descDirty) {
                await ctx.hub.updateChannelDescription(workspaceId, channel.id, trimmedDescription);
            }
            if (subsDirty) {
                await ctx.hub.setChannelSubscribers(workspaceId, channel.id, [...subscriberIds]);
            }
            onSaved?.();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx("div", { style: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 100,
        }, children: _jsxs("form", { onSubmit: onSubmit, style: {
                background: "var(--paper)",
                border: "1px solid var(--hairline)",
                borderRadius: 10,
                padding: 24,
                width: 480,
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                color: "var(--ink)",
            }, children: [_jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 8 }, children: [_jsx("h2", { style: { margin: 0, fontSize: 16, color: "var(--ink)" }, children: "Edit channel" }), _jsxs("span", { style: {
                                fontFamily: "var(--mono)",
                                fontSize: 11,
                                color: "var(--ink-3)",
                            }, children: ["#", channel.name] })] }), _jsxs("label", { style: fieldStyle, children: [_jsx("span", { style: labelStyle, children: "Name" }), _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "general", autoFocus: true, required: true, pattern: "[a-z0-9-]+", maxLength: 40, title: "Lowercase letters, numbers, and hyphens only; max 40 chars", style: inputStyle })] }), _jsxs("label", { style: fieldStyle, children: [_jsx("span", { style: labelStyle, children: "Description" }), _jsx("textarea", { value: description, onChange: (e) => setDescription(e.target.value), placeholder: "What this channel is for\u2026", rows: 2, style: { ...inputStyle, resize: "vertical", minHeight: 48 } })] }), _jsxs("div", { style: fieldStyle, children: [_jsx("span", { style: labelStyle, children: "Subscribed agents" }), loadError ? (_jsxs("div", { style: errorBoxStyle, children: ["Couldn't load the agent list: ", loadError] })) : !allAgents || !initialSubscriberIds ? (_jsx("div", { style: { fontSize: 12, color: "var(--ink-3)", padding: 6 }, children: "Loading\u2026" })) : allAgents.length === 0 ? (_jsx("div", { style: { fontSize: 12, color: "var(--ink-3)", padding: 6 }, children: "No agents in this workspace yet." })) : (_jsx("div", { style: {
                                border: "1px solid var(--hairline)",
                                borderRadius: 6,
                                maxHeight: 180,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                background: "var(--paper-2)",
                            }, children: allAgents.map((a) => {
                                const checked = subscriberIds.has(a.id);
                                const kind = a.runtime_kind ?? "claude-code";
                                return (_jsxs("label", { style: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "6px 10px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid var(--hairline-2)",
                                        fontSize: 13,
                                        color: "var(--ink-2)",
                                    }, children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => toggleSub(a.id) }), _jsx("span", { style: { flex: 1 }, children: a.name }), _jsx("span", { style: {
                                                fontFamily: "var(--mono)",
                                                fontSize: 10,
                                                color: "var(--ink-3)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.04em",
                                            }, children: kind === "agent-v2" ? "v2" : "claude code" })] }, a.id));
                            }) }))] }), newlyAddedV1Names.length > 0 && (_jsxs("div", { style: {
                        fontSize: 11.5,
                        color: "var(--ink-2)",
                        padding: "8px 10px",
                        background: "var(--paper-2)",
                        border: "1px solid var(--hairline)",
                        borderRadius: 6,
                        lineHeight: 1.45,
                    }, children: ["Heads up: Claude Code agents read their subscription list at startup, so", " ", _jsx("span", { className: "ws-mono", style: { fontFamily: "var(--mono)" }, children: newlyAddedV1Names.join(", ") }), " ", "won't see this channel until ", newlyAddedV1Names.length === 1 ? "it's" : "they're", " restarted."] })), error && _jsx("div", { style: errorBoxStyle, children: error }), _jsxs("div", { style: { display: "flex", justifyContent: "flex-end", gap: 8 }, children: [_jsx("button", { type: "button", onClick: onClose, style: { ...btnStyle, background: "transparent", color: "var(--ink-2)" }, children: "Cancel" }), _jsx("button", { type: "submit", disabled: !anyDirty || submitting || !trimmedName, style: {
                                ...btnStyle,
                                background: !anyDirty || !trimmedName ? "var(--paper-3)" : "var(--accent)",
                                color: !anyDirty || !trimmedName ? "var(--ink-3)" : "var(--paper)",
                                cursor: !anyDirty || submitting || !trimmedName ? "not-allowed" : "pointer",
                            }, children: submitting ? "Saving…" : "Save" })] })] }) }));
}
const fieldStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
};
const labelStyle = {
    fontSize: 11,
    color: "var(--ink-3)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
};
const inputStyle = {
    padding: "8px 12px",
    background: "var(--paper-2)",
    border: "1px solid var(--hairline)",
    borderRadius: 6,
    color: "var(--ink)",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
};
const errorBoxStyle = {
    color: "var(--error)",
    fontSize: 12,
    padding: 8,
    border: "1px solid rgba(250,82,82,0.3)",
    borderRadius: 6,
};
const btnStyle = {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    fontFamily: "inherit",
};
//# sourceMappingURL=EditChannelDialog.js.map